import { useEffect, useRef, useMemo, useState } from 'react'
import { render as reactDOMRender } from 'react-dom'
import Image from 'next/image'
import dynamic from 'next/dynamic'
import { isMobile } from 'react-device-detect'
import { useInView } from 'react-intersection-observer'

import { index, detail } from '@/lib/services/blog.service'
import DefaultLayout from '@/layouts/default'
import PinaHead from '@/components/basic/pina-head'
import Container from '@/components/basic/container'
import SocialShare from '@/components/basic/social-share'
import HTMLRender from '@/components/basic/html-render'
import Author from '@/components/section/author'
import SuggestedInvestments from '@/components/section/suggested-investments'
import Heart from '@/components/svg/heart'

const BannerDownload = dynamic(
  () => import('@/components/banner/banner-download'),
  { ssr: false }
)

const BannerDownloadSimple = dynamic(
  () => import('@/components/banner/banner-download-simple'),
  { ssr: false }
)


const BlogArticleDetail = ({ article }) => {
  
  const [image, setImage] = useState(null)
  const contentRef = useRef(null);
  const { ref: authorRef } = useInView({ threshold: 0.2 })
  const { ref: suggestionRef, entry: suggestionEntry } = useInView({
    triggerOnce: true,
    threshold: 0,
    rootMargin: '300px'
  })

  const assets = useMemo(() => {
    return (article?.suggested_investments || []).map(i => i.code)
  }, [article?.suggested_investments])

  useEffect(() => {
    if (article?.featured_image_square || article?.featured_image) {
      const imgObject = isMobile && article?.featured_image_square?._id
        ? article?.featured_image_square
        : article?.featured_image;

      let img = imgObject?.versions.find(v => v.name === 'large');
      // fallback to biggest uploaded image
      if (!img) {
        img = {
          public_url: imgObject?.public_url,
          width: imgObject?.width,
          height: imgObject?.height,
          mime_type: imgObject?.mime_type,
        }
      }

      setImage({ placeholder: imgObject?.placeholder, alt: imgObject?.alt, ...img });
    }
  }, [article?.featured_image_square, article?.featured_image])

  const isSquare = image?.width && image?.height && (image.width/image.height) === 1

  const imageSquareComp = () => {
    if (image?.public_url) {
      return (
        <div className="flex items-center justify-center w-full">
          <div className="w-full my-6 overflow-hidden rounded-md lg:w-6/12">
            <Image
              priority={true}
              quality={90}
              src={image?.public_url}
              alt={image?.alt || article?.title}
              layout="responsive"
              width={image?.width}
              height={image?.height}
              placeholder={image?.placeholder?.base64 ? "blur" : "empty"}
              blurDataURL={image?.placeholder?.base64}
            />
          </div>
        </div>
      )
    }
  }

  const imageNotSquareComp = () => {
    if (image?.public_url) {
      return (
        <div
          className="relative w-full my-6 overflow-hidden rounded-md aspect-w-1 aspect-h-1 md:aspect-w-16 md:aspect-h-9"
        >
          <Image
            priority={true}
            quality={90}
            src={image?.public_url}
            alt={image?.alt || article?.title}
            layout="fill"
            objectFit="cover"
            placeholder={image?.placeholder?.base64 ? "blur" : "empty"}
            blurDataURL={image?.placeholder?.base64}
          />
        </div>
      )
    }
  }

  // Inject banner
  useEffect(() => {
    const injectBanner = () => {
      if (
        contentRef?.current &&
        contentRef.current.querySelector("span#ca-banner")
      ) {
        return
      }

      if (!article?.banner?._id) {
        return
      }

      const paragraphs = contentRef?.current?.getElementsByTagName("p") || []
      const injectAtIndex = 2 // start from index 0

      for (let i = 0; i < paragraphs.length; i++) {
        if (i > injectAtIndex) {
          break
        }

        if (i === injectAtIndex) {
          const targetNode = document.createElement("span")
          targetNode.setAttribute("id", "ca-banner")
          targetNode.setAttribute("class", "ignore")
          paragraphs[i].after(targetNode)

          let bannerProps = {
            isPopUp: false,
            type: 'pn-banner-dl-ca',
            className: 'mb-4'
          }
          let BannerComponent = null
          if (article?.banner?.slug === 'simple') {
            BannerComponent = <BannerDownloadSimple {...bannerProps} />
          } else {
            if (article?.banner?.title) {
              bannerProps.title = article?.banner?.title
            }

            if (article?.banner?.description) {
              bannerProps.description = article?.banner?.description
            }

            if (article?.banner?.mobile_image?.public_url) {
              bannerProps.imgMobileObject = {
                url: article.banner.mobile_image.public_url,
                placeholder: article.banner.mobile_image.placeholder,
                width: article.banner.mobile_image.width,
                height: article.banner.mobile_image.height,
                alt: article.banner.mobile_image.alt,
              }
            }

            if (article?.banner?.desktop_image?.public_url) {
              bannerProps.imgDesktopObject = {
                url: article.banner.desktop_image.public_url,
                placeholder: article.banner.desktop_image.placeholder,
                width: article.banner.desktop_image.width,
                height: article.banner.desktop_image.height,
                alt: article.banner.desktop_image.alt,
              }
            }

            BannerComponent = <BannerDownload {...bannerProps} />
          }

          reactDOMRender(BannerComponent, document.querySelector("#ca-banner"))
          break
        }
      }
    }

    if (contentRef?.current) {
      let intercomEl = document.getElementsByClassName(
        "intercom-lightweight-app-launcher"
      );

      const interElIframe = document.getElementsByClassName(
        "intercom-launcher-frame"
      );
      
      if (interElIframe.length > 0) {
        intercomEl = interElIframe;
      }

      if (intercomEl[0]) {
        intercomEl[0].style.transition = "transform .2s ease-in-out";
        intercomEl[0].style.transform = "translateY(0)";
      }
      return injectBanner();
    }
  }, [contentRef, article?.banner])

  return (
    <>
      <PinaHead data={{
        ...article?.metadata,
        url: article?.slug && `/articles/${article?.slug}`,
        image: image?.public_url,
        og: {
          image_type: image?.mime_type,
          image_width: null,
          image_height: null,
        },
        twitter: {
          image_alt: image?.alt
        }
      }} />

      <article className="my-4 pt-16 container mx-auto">
        <Container size="small" spacing="px-0 md:px-4">
          {
            article.title && 
            <h1 className="mb-2 text-3xl font-bold text-black">{article.title}</h1>
          }

          {
            article.excerpt && 
            <p className="text-base italic text-gray-500">{article.excerpt}</p>
          }

          <div className="flex flex-row items-center my-4">
            <button type="button" className="flex flex-row items-center pb-1 mr-2 focus:outline-none cursor-default" disabled={true}>
              <Heart className={`w-5 h-5 stroke-current stroke-1 text-olive`}/>
              <span className="ml-1 text-olive">
                {article.like_total <= 0 ? '0' : article.like_total}
              </span>
            </button>
            <SocialShare />
          </div>

          { isSquare ? imageSquareComp() : imageNotSquareComp() }

          <HTMLRender ref={contentRef} htmlString={article.content} />

          {
            assets &&
            assets.length > 0 &&
            <SuggestedInvestments ref={suggestionRef} intersectEntry={suggestionEntry} assets={assets || []} />
          }

          <Author ref={authorRef} name="Pina" role="Content Writer Team" className="mt-4 mb-10" />

        </Container>
      </article>
    </>
  )
}

BlogArticleDetail.getLayout = function getLayout(page) {
  return (
    <DefaultLayout>
      {page}
    </DefaultLayout>
  )
}

export async function getStaticPaths() {
  // Initial generated articles are 200
  const { data } = await index({ limit: 200 })

  const paths = (data || []).map(({ slug }) => ({
    params: {
      slug: slug
    },
  }))

  return { paths, fallback: 'blocking' }
}

export async function getStaticProps({ params }) {
  const { success, data, code, message } = await detail(params.slug)

  if (!success) {
    console.error(`[ERROR] blog detail`, { code, message, data })
    return {
      notFound: true
    }
  }

  return {
    props: {
      article: data
    },
    revalidate: 60,
  }
}

export default BlogArticleDetail